import {
  PhotoIcon,
  XMarkIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
} from '@heroicons/react/24/solid';
import React from 'react';
import { GroupIcon } from 'src/components/icons/GroupIcon';
import { NodeIcon } from 'src/components/icons/NodeIcon';
import { appConfig } from 'src/config';
import { useUndoRedo } from 'src/context/undo-redo';
import { twMerge } from 'tailwind-merge';

import { EDataType } from '../models/node-data';

interface IAction {
  id: string;
  title: string;
  icon: React.ReactNode;
  tooltip: string;
  onClick?: () => void;
  onDragStart?: (event: React.DragEvent<HTMLDivElement>) => void;
  disabled?: boolean;
  draggable?: boolean;
  isEnabled: boolean;
}

export default function FlowControls() {
  const { undo, redo, canUndo, canRedo, clearAll, canClearAll } = useUndoRedo();

  const actionsList: IAction[][] = [
    [
      {
        id: 'group',
        title: 'Group',
        icon: <GroupIcon className="h-8 w-8" />,
        tooltip: 'Drag to create a group',
        onDragStart: (event: React.DragEvent<HTMLDivElement>) => {
          const dragElement = document.createElement('div');
          dragElement.innerHTML = `<span class="sr-only">Group</span>`;
          dragElement.style.position = 'absolute';
          dragElement.style.top = '-9999px';
          dragElement.style.width = '400px';
          dragElement.style.height = '200px';
          dragElement.className =
            'bg-purple-500/10 shadow-lg border-blue-500 rounded-md border-2';
          document.body.appendChild(dragElement);
          event.dataTransfer.setDragImage(dragElement, 50, 25);
          event.dataTransfer.setData('application/reactflow', EDataType.Group);
          event.dataTransfer.effectAllowed = 'move';
          setTimeout(() => document.body.removeChild(dragElement), 0);
        },
        draggable: true,
        isEnabled: appConfig.enableGroupingNode,
      },
      {
        id: 'note',
        title: 'Note',
        icon: <NodeIcon className="h-8 w-8" />,
        tooltip: 'Drag to create a note',
        onDragStart: (event: React.DragEvent<HTMLDivElement>) => {
          const dragElement = document.createElement('div');
          dragElement.innerHTML = `<div class="absolute bottom-0 left-0 h-2 w-full bg-yellow-300 opacity-50" />`;
          dragElement.style.position = 'absolute';
          dragElement.style.top = '-9999px';
          dragElement.style.width = '100px';
          dragElement.style.height = '50px';
          dragElement.className =
            'relative rotate-2 transform rounded-md bg-yellow-200 p-4 shadow-xl';
          document.body.appendChild(dragElement);
          event.dataTransfer.setDragImage(dragElement, 50, 25);
          event.dataTransfer.setData('application/reactflow', EDataType.Note);
          event.dataTransfer.effectAllowed = 'move';
          setTimeout(() => document.body.removeChild(dragElement), 0);
        },
        draggable: true,
        isEnabled: true,
      },
      {
        id: 'image',
        title: 'Image',
        icon: <PhotoIcon className="h-8 w-8" />,
        tooltip: 'Drag to create an image',
        onDragStart: (event: React.DragEvent<HTMLDivElement>) => {
          const dragElement = document.createElement('div');
          dragElement.innerHTML = `<span class="sr-only">Image</span>`;
          dragElement.style.position = 'absolute';
          dragElement.style.top = '-9999px';
          dragElement.style.width = '200px';
          dragElement.style.height = '200px';
          dragElement.className =
            'bg-black/10 shadow-lg border-blue-500 rounded-md border-2';
          document.body.appendChild(dragElement);
          event.dataTransfer.setDragImage(dragElement, 50, 25);
          event.dataTransfer.setData('application/reactflow', EDataType.Image);
          event.dataTransfer.effectAllowed = 'move';
          setTimeout(() => document.body.removeChild(dragElement), 0);
        },
        draggable: true,
        isEnabled: true,
      },
    ],
    [
      {
        id: 'undo',
        title: 'Undo',
        icon: <ArrowUturnLeftIcon className="h-6 w-6" />,
        tooltip: 'Undo',
        onClick: undo,
        disabled: !canUndo,
        isEnabled: true,
      },
      {
        id: 'redo',
        title: 'Redo',
        icon: <ArrowUturnRightIcon className="h-6 w-6" />,
        tooltip: 'Redo',
        onClick: redo,
        disabled: !canRedo,
        isEnabled: true,
      },
      {
        id: 'clear-all',
        title: 'Clear All',
        icon: <XMarkIcon className="h-6 w-6" />,
        tooltip: 'Clear All',
        onClick: clearAll,
        disabled: !canClearAll,
        isEnabled: true,
      },
    ],
  ];

  return (
    <div className="absolute left-0 top-1/2 flex -translate-y-1/2 flex-col gap-1 p-2">
      {actionsList.map((actions, index) => (
        <div
          key={index}
          className="flex flex-col gap-1 rounded-md border bg-white p-1 shadow-md"
        >
          {actions.map((action, subIndex) => (
            <ActionButton key={`${index}-${subIndex}`} action={action} />
          ))}
        </div>
      ))}
    </div>
  );
}

const ActionButton = ({ action }: { action: IAction }) => {
  const {
    id,
    tooltip,
    icon,
    isEnabled,
    disabled,
    onClick,
    draggable,
    ...rest
  } = action;
  if (!isEnabled) return null;
  return (
    <button
      data-testid={`control-action-button-${id}`}
      className={twMerge([
        'flex items-center justify-center rounded-sm p-1',
        'hover:bg-blue-200',
        'h-10 w-10',
        draggable && 'cursor-grab',
        onClick && 'cursor-pointer',
        disabled && 'cursor-not-allowed opacity-50',
      ])}
      onClick={onClick}
      draggable={draggable}
      disabled={disabled}
      data-tooltip-id="app-tooltip"
      data-tooltip-content={tooltip}
      type="button"
      {...(rest as any)}
    >
      {icon}
    </button>
  );
};
